<template>
  <component
    :is="collapsibleComponent"
    title="Historique des sociétés liées"
    icon="fas fa-sitemap"
    :context="context"
    storage-key="societes-liees-info"
    :use-grid="false"
    :count="count"
    @toggle="onToggle"
  >
    <LinkedCompaniesContent
      :same-group="sameGroup"
      :loading="loading"
      :can-search-ai="loaded && !!reference?.siret"
      :ai-companies="aiCompanies"
      :ai-loading="aiLoading"
      :ai-searched="aiSearched"
      :ai-error="aiError"
      @search-ai="searchAi"
    />
  </component>
</template>

<script setup>
/**
 * Sociétés liées à un client — un seul comportement pour les deux points
 * d'entrée (fiche contact / fiche devis) : avant ce composant, ces fichiers
 * étaient identiques à trois détails près (la prop d'identité, le `context`
 * passé à CollapsibleSection, le loader appelé). `email` et `quoteId` sont
 * donc optionnelles ici, et le loader se choisit selon celle qui est
 * renseignée.
 *
 * `CollapsibleSection` reste une prop plutôt que codée en dur, pour ne pas
 * lier ce composant partagé à un chemin d'import précis.
 *
 * Chargement paresseux : le rapprochement balaie toute la base professionnelle
 * (cf. useLinkedCompanies) et ne doit donc partir qu'à l'ouverture RÉELLE de
 * la section, pas au montage de la page. `@toggle` est émis par
 * CollapsibleSection au premier dépli ET, si l'état mémorisé rouvre la
 * section dès le montage, une fois à ce moment-là aussi — un seul
 * gestionnaire couvre les deux cas.
 */
import { computed } from 'vue'
import LinkedCompaniesContent from './LinkedCompaniesContent.vue'
import { useLinkedCompanies } from '../../composables/contacts/useLinkedCompanies.js'
import { useSimilarCompaniesAi } from '../../composables/contacts/useSimilarCompaniesAi.js'

const props = defineProps({
  email: {
    type: String,
    default: null
  },
  /** Établissement de la fiche, quand la personne en représente plusieurs. */
  siret: {
    type: String,
    default: ''
  },
  quoteId: {
    type: [String, Number],
    default: null
  },
  context: {
    type: String,
    required: true
  },
  /** Le composant CollapsibleSection à monter, fourni par l'appelant. */
  collapsibleComponent: {
    type: [Object, Function],
    required: true
  }
})

const { sameGroup, reference, loading, loaded, loadForContact, loadForQuote } = useLinkedCompanies()
const {
  companies: aiCompanies,
  loading: aiLoading,
  searched: aiSearched,
  error: aiError,
  searchForContact,
  searchForQuote
} = useSimilarCompaniesAi()

const count = computed(() => (loaded.value ? sameGroup.value.length + aiCompanies.value.length : null))

const searchAi = () => {
  if (props.email) searchForContact(props.email, props.siret)
  else if (props.quoteId) searchForQuote(props.quoteId)
}

const onToggle = (isCollapsed) => {
  if (isCollapsed) return
  if (props.email) loadForContact(props.email, props.siret)
  else if (props.quoteId) loadForQuote(props.quoteId)
}
</script>
